import { useCallback, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'

import { AppUpdateDialog } from '@/components/pwa/app-update-dialog'
import {
  hasNewAppBuildAvailable,
  markAppUpdateAttempt,
  shouldSuppressAppUpdateNotification,
} from '@/lib/app-build-id'
import {
  applyPlayUpdate,
  checkPlayUpdateAvailable,
} from '@/lib/play-in-app-update'
import {
  forceAppUpdateReload,
  registerPwaServiceWorker,
} from '@/lib/register-pwa'

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000

type UpdateStrategy = 'pwa' | 'reload' | 'play-flexible' | 'play-immediate'

function shouldCheckWebVersion(): boolean {
  return import.meta.env.PROD && !Capacitor.isNativePlatform()
}

export function AppUpdateNotifier() {
  const [open, setOpen] = useState(false)
  const reloadPageRef = useRef<(() => Promise<void>) | null>(null)
  const reloadAppRef = useRef<(() => Promise<void>) | null>(null)
  const updateStrategyRef = useRef<UpdateStrategy>('reload')
  const updateNotifiedRef = useRef(false)

  const notifyUpdate = useCallback((strategy: UpdateStrategy) => {
    if (updateNotifiedRef.current) return
    if (shouldSuppressAppUpdateNotification()) return

    updateNotifiedRef.current = true
    updateStrategyRef.current = strategy
    setOpen(true)
  }, [])

  const reloadApp = useCallback(async () => {
    markAppUpdateAttempt()
    setOpen(false)

    const preferred =
      reloadPageRef.current ?? reloadAppRef.current ?? null

    await forceAppUpdateReload(preferred)
  }, [])

  const handleUpdate = useCallback(() => {
    const strategy = updateStrategyRef.current

    if (strategy === 'pwa' || strategy === 'reload') {
      void reloadApp()
      return
    }

    if (strategy === 'play-flexible' || strategy === 'play-immediate') {
      void applyPlayUpdate(
        strategy === 'play-immediate' ? 'immediate' : 'flexible',
      )
      return
    }

    void reloadApp()
  }, [reloadApp])

  useEffect(() => {
    if (!shouldCheckWebVersion()) return

    reloadAppRef.current = registerPwaServiceWorker((reloadPage) => {
      reloadPageRef.current = reloadPage
      notifyUpdate('pwa')
    })
  }, [notifyUpdate])

  useEffect(() => {
    if (!shouldCheckWebVersion()) return

    let intervalId: number | undefined

    const checkVersion = async () => {
      if (!navigator.onLine) return
      if (shouldSuppressAppUpdateNotification()) return

      const registration = await navigator.serviceWorker?.getRegistration()
      if (registration?.waiting) {
        notifyUpdate('pwa')
        return
      }

      const hasUpdate = await hasNewAppBuildAvailable()
      if (hasUpdate) {
        // Build id mudou, mas o SW pode ainda não ter disparado onNeedRefresh
        notifyUpdate(reloadPageRef.current ? 'pwa' : 'reload')
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion()
      }
    }

    void checkVersion()

    intervalId = window.setInterval(() => {
      void checkVersion()
    }, VERSION_CHECK_INTERVAL_MS)

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      if (intervalId !== undefined) window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [notifyUpdate])

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return
    }

    const timeoutId = window.setTimeout(() => {
      void checkPlayUpdateAvailable().then((mode) => {
        if (!mode) return
        notifyUpdate(mode === 'immediate' ? 'play-immediate' : 'play-flexible')
      })
    }, 3000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [notifyUpdate])

  return <AppUpdateDialog open={open} onUpdate={handleUpdate} />
}
